import { useTranslation } from 'react-i18next'

import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import {
  AdditionalTourPaymentIssue,
  startAdditionalTourPayment,
} from './start-additional-tour-payment'
import type { TourDocumentView } from './types'
import { tourDocumentQueryOptions } from './use-tour-document'

export const useAdditionalTourPayment = (
  document: TourDocumentView,
  hasRefreshError: boolean,
) => {
  const { t } = useTranslation()
  const { showErrorDialog } = useApiErrorDialog()
  const queryClient = useQueryClient()
  const documentQuery = tourDocumentQueryOptions(document.id)
  const { queryKey } = documentQuery
  const mutationKey = ['tours', 'additional-payment', document.id]
  const isPaying = useIsMutating({ mutationKey }) > 0
  const isRefreshing = useIsFetching({ queryKey }) > 0
  const isBusy = () =>
    queryClient.isMutating({ mutationKey }) > 0 ||
    queryClient.isFetching({ queryKey }) > 0

  const showIssue = (issue: AdditionalTourPaymentIssue) =>
    showErrorDialog(
      t(
        issue === 'startFailed'
          ? 'tourContract.paymentStartError'
          : `tourAdditionalPayment.${issue}`,
      ),
    )

  const refreshDocument = () => queryClient.fetchQuery(documentQuery)

  const mutation = useMutation({
    mutationKey,
    mutationFn: (amount: string | null) =>
      startAdditionalTourPayment(document, amount),
    retry: false,
    networkMode: 'always',
    onSettled: async (issue, error) => {
      if (!error && !issue) return
      // A failed refresh stays in the query state and blocks another payment.
      await refreshDocument().catch(() => undefined)
      if (error) showIssue('requestFailed')
      else if (issue) showIssue(issue)
    },
  })

  const pay = (amount: string | null = null) => {
    if (isBusy()) return
    if (hasRefreshError) {
      showIssue('refreshFailed')
      return
    }

    mutation.mutate(amount)
  }

  const refresh = async () => {
    if (isBusy()) return
    try {
      await refreshDocument()
    } catch {
      showIssue('refreshFailed')
    }
  }

  return {
    pay,
    refresh,
    isPaying,
    isRefreshing,
    isBlocked: isPaying || isRefreshing || hasRefreshError,
  }
}
